import { ApiError, AppError, Language } from '@/types/types';
import { Locale, formatDistance } from 'date-fns';
import { flatten, groupBy } from 'lodash';
import { isAxiosError } from 'axios';
import { DateValue } from '@mantine/dates';
import { ErrorCode } from '@/configs/errorCode';
import {
	AttributeWithValues,
	CreatorOptions,
	CreatorState,
	SavedCreatorState,
	SelectedSlot,
} from '@/types/creator';
import { AttributeValue } from '@/api/AttributeValueApi';
import { ProductModule, ProductModuleType } from '@/api/ProductModuleApi';
import { SlotType } from '@/renderer/objects/ConnectingSlot';
import { SerializedRenderer } from '@/renderer/types';
import { CreatorProductModule } from '@/api/CreatorProductModuleApi';
import APP_CONFIG from '@/configs/appConfig';
import moment from 'moment';

export function formatDateForInput(date: moment.MomentInput, format?: string) {
	return moment(date, format).format('YYYY-MM-DD');
}

export function formatDateForPresentation(date: moment.MomentInput) {
	if (!date) return '-';
	return moment(date).format('DD.MM.YYYY');
}

export function formatTimeForPresentation(
	date: moment.MomentInput | string = new Date()
) {
	return moment(date).format('HH:mm');
}

export function formatExactDate(date: moment.MomentInput = new Date()) {
	return `${formatDateForPresentation(date)}, ${formatTimeForPresentation(
		date
	)}`;
}

const PASSWORD_REGEX = /^(?=\D*\d)[^ ]{6,}$/;

/**
 *
 * @param password one number and at least 8
 * @returns
 */
export function validatePassword(password: string) {
	return PASSWORD_REGEX.test(password);
}

const PHONE_REGEX =
	/^\s*(?:\+?(\d{1,3}))?([-. (]*(\d{3})[-. )]*)?((\d{3})[-. ]*(\d{2,4})(?:[-.x ]*(\d+))?)\s*$/;

/**
 *
 * @param phoneNumber validate international
 * @returns
 */
export function validatePhoneNumber(phoneNumber: string) {
	return PHONE_REGEX.test(phoneNumber) && phoneNumber.length >= 9;
}

/**
 *
 * @param accountNumber validate polish
 * @returns
 */
export function validateAccountNumber(accountNumber: string) {
	const trimmed = accountNumber.trim();
	return /\d{26}/.test(trimmed);
}

/**
 *
 * @param postalCode validate polish
 * @returns
 */
export function validatePostalCode(postalCode: string) {
	const trimmed = postalCode.trim();
	return /^[0-9]{2}-[0-9]{3}/.test(trimmed);
}

export function isValidDateObj(date: moment.MomentInput) {
	return moment(date).isValid();
}

export function incrementDate(date: moment.MomentInput) {
	return moment(date).add(1, 'day').toDate();
}

export function randomDate(start: Date, end: Date) {
	return new Date(
		start.getTime() + Math.random() * (end.getTime() - start.getTime())
	);
}

export function sortByDate(
	dateA: string | Date,
	dateB: string | Date,
	order: 'asc' | 'desc' = 'asc'
) {
	const first = moment(dateA).valueOf(),
		second = moment(dateB).valueOf();

	return order === 'asc' ? first - second : second - first;
}

export function stripHTML(string: string) {
	return string.replace(/(<([^>]+)>)/gi, '');
}

export function handleUnload(e: BeforeUnloadEvent) {
	e.preventDefault();
	e.returnValue = '';
}

export const ID_REGEX =
	/[a-f0-9]{8}-?[a-f0-9]{4}-?[a-f0-9]{4}-?[89ab][a-f0-9]{3}-?[a-f0-9]{12}/g;

export function getIdsFromText(text: string) {
	return [...new Set(flatten([...text.matchAll(ID_REGEX)]))];
}

export function fileTypeToHeader(fileType: 'csv' | 'xlsx' | 'pdf') {
	switch (fileType) {
		case 'xlsx':
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
		case 'csv':
			return 'text/csv';
		case 'pdf':
			return 'application/pdf';
		default:
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
	}
}

export function getContent(conent: Record<string, string>, slug: string) {
	return conent[slug];
}

export function formatDateFromNow(date: Date, locale: Locale) {
	return formatDistance(date, new Date(), {
		locale,
		includeSeconds: true,
		addSuffix: true,
	});
}

export const formatPrice = (value: number, opts?: Intl.NumberFormatOptions) => {
	const formatter = new Intl.NumberFormat('pl', {
		style: 'currency',
		currency: 'PLN',
		currencyDisplay: 'code',
		maximumFractionDigits: !value ? 0 : undefined,
		...opts,
	});

	return formatter.format(value);
};

export const isApiError = (error: unknown): error is ApiError => {
	return (
		!!(error as AppError).status &&
		!!(error as AppError).stack &&
		!!(error as ApiError).message
	);
};

export const isAppError = (error: unknown): error is AppError => {
	return !!(error as AppError).status && !!(error as AppError).stack;
};

export const getErrorCodes = (error: unknown) => {
	if (isAxiosError<ApiError>(error) && error.response)
		return Array.isArray(error.response.data.message)
			? error.response.data.message
			: [error.response.data.message];

	return [ErrorCode.GENERIC];
};

export const errorCodesToMessage = (errorCodes: ErrorCode[]) =>
	errorCodes.map((code) => `errors.${code}`).join(', ');

export function getFileSrc(path?: string) {
	return `${import.meta.env.VITE_API_URL_UPLOADS}${path}`;
}

export function formatDateForApi(date: DateValue) {
	if (!date) return null;
	return moment(date).format('YYYY-MM-DD');
}

export function isDefaultLanguage(language: Language): language is 'PL' {
	return language === APP_CONFIG.DEFAULT_LANGUAGE;
}

export function natSort(nameA = '', nameB = '', order: 'asc' | 'desc' = 'asc') {
	let a: string | number = Number(nameA),
		b: string | number = Number(nameB);

	if (isNaN(a) || isNaN(b)) {
		return order === 'asc'
			? nameA.localeCompare(nameB)
			: nameB.localeCompare(nameA);
	}

	return order === 'asc' ? a - b : b - a;
}

export function inputWidthValue(value: string) {
	return `${value.length + 3}ch`;
}

export function checkProductModuleMatching(
	productModule: ProductModule,
	selectedSlot: SelectedSlot,
	slotsProductModule?: ProductModule
) {
	if (!slotsProductModule) return false;

	const productModuleType = productModule.moduleType;

	if (slotsProductModule.modulesBlacklistIds.includes(productModule.id))
		return false;
	if (productModule.modulesBlacklistIds.includes(slotsProductModule.id))
		return false;

	switch (selectedSlot.slotType) {
		case SlotType.LEFT:
			return [ProductModuleType.LEFT, ProductModuleType.LINE].includes(
				productModuleType
			);
		case SlotType.RIGHT:
			return [ProductModuleType.RIGHT, ProductModuleType.LINE].includes(
				productModuleType
			);

		default:
			return false;
	}
}

export function checkProductModuleSwapMatching(
	productModule: ProductModule,
	swapDetails: CreatorOptions['swapDetails'],
	creatorProductModules: CreatorProductModule[]
) {
	const { slotTypes, modulesIds, creatorProductModule } = swapDetails!;

	if (creatorProductModule?.productModule.id === productModule.id) return false;

	const connectedModules = creatorProductModules.filter((cpm) =>
		modulesIds.includes(cpm.id!)
	);

	for (const connectedModule of connectedModules) {
		if (
			connectedModule.productModule.modulesBlacklistIds.includes(
				productModule.id
			)
		)
			return false;
		if (
			productModule.modulesBlacklistIds.includes(
				connectedModule.productModule.id
			)
		)
			return false;
	}

	const productModuleType = productModule.moduleType;
	const forbiddenModuleTypes = [];

	if (slotTypes.includes(SlotType.LEFT) && slotTypes.includes(SlotType.RIGHT))
		forbiddenModuleTypes.push(ProductModuleType.LEFT, ProductModuleType.RIGHT);
	if (slotTypes.includes(SlotType.LEFT))
		forbiddenModuleTypes.push(ProductModuleType.LEFT);
	if (slotTypes.includes(SlotType.RIGHT))
		forbiddenModuleTypes.push(ProductModuleType.RIGHT);

	return !forbiddenModuleTypes.includes(productModuleType);
}

export function groupAttributeValues(attributeValues: AttributeValue[]) {
	const grouped = groupBy(attributeValues, 'attribute.id');

	const result = [];

	for (const values of Object.values(grouped)) {
		result.push({
			...values[0].attribute,
			values,
		});
	}

	return result;
}

export function roundToHalf(num: number) {
	return Math.round(num * 2) / 2;
}

export function mapAttributesByRelation(
	attributesWithValues: AttributeWithValues[],
	selectedAttributeValues: AttributeValue[]
) {
	return attributesWithValues
		.map((attribute) => {
			const values = attribute.values
				.filter((value) => {
					if (!value.relation) return true;

					return selectedAttributeValues.some(
						(attrVal) => attrVal.id === value.relation!.objectId
					);
				})
				.sort((a, b) => a.listOrder - b.listOrder);

			return { ...attribute, values: structuredClone(values) };
		})
		.filter((attr) => !!attr.values.length)
		.sort((a, b) => a.listOrder - b.listOrder);
}

export function generateProjectPayload(
	{
		options,
		project,
		selectedProductModuleCollection,
		selectedPreset,
		attributesWithValues,
		globalSelectedAttributeValues,
	}: CreatorState,
	projectRenderer: SerializedRenderer
): SavedCreatorState {
	return {
		project,
		selectedProductModuleCollection,
		selectedPreset,
		attributesWithValues,
		globalSelectedAttributeValues,
		options,
		projectRenderer,
	};
}

export function saveProjectToLocal(
	state: CreatorState,
	rendererState: SerializedRenderer
) {
	if (import.meta.env.DEV) return;

	localStorage.setItem(
		APP_CONFIG.TEMP_PROJECT_STORAGE_KEY,
		JSON.stringify(generateProjectPayload(state, rendererState))
	);
}
